import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Typography, Stack, Box, IconButton } from "@mui/material";
import { CalibrationCoverage } from "./CalibrationCoverage";
import { CalibrationProgressCard } from "./CalibrationProgressCard";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getCalibrationDistribution,
  getMissingSamples,
  isGoalComplete,
} from "./calibration-distribution";
import { useNavigate, useParams, useRouteLoaderData } from "react-router";
import { FieldsData } from "../../../../../../../../API/fields";
import { SelectedAppleFeature } from "../../../../../../../../Component/Map/Layers/apple-count/calibration/AppleCountCalibrationLayer";
import {
  CalibrationItem,
  MobileStep,
  MINIMUM_ITEMS,
  AppleFeature,
  AppleCountCalibrationFeatureCollection,
} from "../Calibration.types";
import { YieldBucket } from "../../../../../../../../Component/Map/Layers/apple-count/apple-count-helper";
import { AverageWeightInput } from "./AverageWeightInput";
import { CalibrationActionArea } from "./CalibrationActionArea";
import type { SubmitNotice } from "../useCalibration";

export function CalibrationSidebar({
  geojson,
  runCalibration,
  state,
  actions,
  buckets,
  mobileStep,
  setMobileStep,
  edgeSampleCount,
}: {
  geojson: AppleCountCalibrationFeatureCollection;
  runCalibration: (averageWeight: number) => Promise<void>;
  state: {
    calibrationSet: CalibrationItem[];
    averageWeight: number | null;
    selectedFeatureId: number | null;
    submitNotice: SubmitNotice | null;
    loading: boolean;
  };
  buckets: YieldBucket[];
  actions: {
    removeCalibrationItem: (plotid: number) => void;
    editCalibrationItem: (plotId: number) => void;
    selectFeature: (feature: SelectedAppleFeature) => void;
    setAverageWeight: (averageWeight: number | null) => void;
  };
  mobileStep: MobileStep;
  setMobileStep: (step: MobileStep) => void;
  edgeSampleCount: number;
}) {
  const { t } = useTranslation();
  const { fields } = useRouteLoaderData("customer") as FieldsData;
  const { fieldId, customerId, resultSetId } = useParams();
  const navigate = useNavigate();

  const field = fields.features.find(
    (feature) => `${feature.properties.id}` === fieldId,
  )?.properties;

  if (!field) {
    return null;
  }

  const {
    calibrationSet,
    averageWeight,
    selectedFeatureId,
    submitNotice,
    loading,
  } = state;
  const {
    selectFeature,
    editCalibrationItem,
    removeCalibrationItem,
    setAverageWeight,
  } = actions;

  const calibrationCount = calibrationSet.length;

  const coverage = useMemo(
    () => getCalibrationDistribution(calibrationSet, buckets),
    [buckets, calibrationSet],
  );

  const goalComplete = isGoalComplete(calibrationCount, coverage);

  const missingSamples = getMissingSamples(coverage);

  const canCalibrate =
    goalComplete && averageWeight !== null && averageWeight > 0;

  const goBack = () =>
    navigate(
      `/customer/${customerId}/field/${fieldId}/result-set/${resultSetId}`,
    );

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 480 },
        height: { xs: "100%", md: "100%" },
        flexShrink: 0,
        display: {
          xs: mobileStep === "calibration" ? "flex" : "none",
          md: "flex",
        },
        flexDirection: "column",
        borderRight: { xs: 0, md: 1 },
        borderColor: "divider",
        bgcolor: "background.paper",
        zIndex: 10,
        overflowY: { md: "auto" },
      }}
    >
      <Box sx={{ p: 4, flexGrow: 1, paddingTop: { xs: 4, md: 0 } }}>
        <Stack direction="row" alignItems="center" mb={1}>
          <IconButton
            onClick={() => setMobileStep("map")}
            sx={{
              mr: 1,
              display: { xs: "inline-flex", md: "none" },
              ml: -1,
            }}
          >
            <ExpandMoreIcon sx={{ transform: "rotate(90deg)" }} />
          </IconButton>
          <IconButton
            onClick={goBack}
            sx={{
              mr: 1,
              display: { xs: "none", md: "inline-flex" },
              ml: -1,
            }}
          >
            <ExpandMoreIcon sx={{ transform: "rotate(90deg)" }} />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" mb="8px">
            {t("header.calibrate.title")}
          </Typography>
        </Stack>
        <Typography variant="subtitle1" mb="12px">
          {`${t("heading.Field")} ${field.name}`}
        </Typography>
        <CalibrationProgressCard
          calibrationItemCount={calibrationCount}
          minimumItems={MINIMUM_ITEMS}
          goalComplete={goalComplete}
          missingSamples={missingSamples}
        />
        <CalibrationCoverage
          calibrationItemCount={calibrationCount}
          calibrationSet={calibrationSet}
          coverage={coverage}
          onSelect={(item) => {
            const feature = geojson.features.find(
              (f: AppleFeature) => f.properties?.plot_id === item.plotId,
            );

            if (feature) selectFeature(feature);
          }}
          onEdit={editCalibrationItem}
          onDelete={removeCalibrationItem}
          activePlotId={selectedFeatureId}
        />
        <AverageWeightInput value={averageWeight} onChange={setAverageWeight} />
      </Box>

      <CalibrationActionArea
        canCalibrate={canCalibrate}
        loading={loading}
        coverage={coverage}
        averageAppleWeight={averageWeight}
        onRunCalibration={() => void runCalibration(averageWeight!)}
        edgeSampleCount={edgeSampleCount}
        sampleCount={calibrationCount}
        notice={submitNotice}
      />
    </Box>
  );
}
